/**
 * Patrol checkpoints — admin management.
 *
 * Create / edit / deactivate checkpoints. Each checkpoint has a printable
 * QR token (hooddesk-patrol:<id>:<secret>); admin can reveal or rotate the
 * secret. Sidebar shows the latest scans across all guards.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MapPin, Plus, RotateCcw, Eye, QrCode, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDateTime } from '@/lib/format';

interface Checkpoint {
  id: string;
  name: string;
  location_label: string | null;
  position_index: number;
  active: boolean;
}

interface PatrolLog {
  id: string;
  scanned_at: string;
  checkpoint: { id: string; name: string };
  guard: { id: string; full_name: string };
}

const createSchema = z.object({
  name: z.string().min(2).max(120),
  location_label: z.string().max(200).optional(),
  position_index: z.coerce.number().int().min(0).max(1000).default(0)
});

type CreateForm = z.infer<typeof createSchema>;

export function PatrolPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [qrFor, setQrFor] = useState<{ id: string; name: string; token: string } | null>(null);

  const checkpoints = useQuery({
    queryKey: ['patrol', 'checkpoints'],
    queryFn: async () => (await get<{ items: Checkpoint[] }>('/v1/patrol/checkpoints')).items
  });

  const logs = useQuery({
    queryKey: ['patrol', 'logs'],
    queryFn: async () => (await get<{ items: PatrolLog[] }>('/v1/patrol/logs', { params: { limit: 30 } })).items
  });

  const form = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: '', location_label: '', position_index: 0 }
  });

  const createMut = useMutation({
    mutationFn: async (data: CreateForm) => {
      await post('/v1/patrol/checkpoints', data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patrol', 'checkpoints'] });
      setCreateOpen(false);
      form.reset();
      toast.success('Checkpoint added');
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const toggleMut = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      await patch(`/v1/patrol/checkpoints/${id}`, { active });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['patrol', 'checkpoints'] }),
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const revealMut = useMutation({
    mutationFn: async (cp: Checkpoint) => {
      const res = await get<{ id: string; name: string; qr_token: string }>(`/v1/patrol/checkpoints/${cp.id}/qr-token`);
      return res;
    },
    onSuccess: (res) => setQrFor({ id: res.id, name: res.name, token: res.qr_token }),
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const rotateMut = useMutation({
    mutationFn: async (cp: Checkpoint) => {
      const res = await post<{ id: string; qr_token: string }>(`/v1/patrol/checkpoints/${cp.id}/rotate-qr`, {});
      return { id: cp.id, name: cp.name, token: res.qr_token };
    },
    onSuccess: (res) => {
      setQrFor(res);
      toast.success('Secret rotated — print the new QR');
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Patrol checkpoints"
        description="Printable QR codes guards scan during rounds."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> New checkpoint
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {checkpoints.isLoading ? (
            [1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)
          ) : (checkpoints.data ?? []).length === 0 ? (
            <EmptyState
              title="No checkpoints yet"
              description="Create your first patrol checkpoint to print a QR."
              icon={<MapPin className="h-10 w-10 text-slate-400" />}
            />
          ) : (
            (checkpoints.data ?? []).map((cp) => (
              <Card key={cp.id}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-slate-700">
                    {cp.position_index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 truncate">{cp.name}</h3>
                      <Badge className={cp.active ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}>
                        {cp.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    {cp.location_label ? <p className="text-sm text-slate-500">{cp.location_label}</p> : null}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" title="View QR" onClick={() => revealMut.mutate(cp)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" title="Rotate secret" onClick={() => rotateMut.mutate(cp)}>
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      title={cp.active ? 'Deactivate' : 'Activate'}
                      onClick={() => toggleMut.mutate({ id: cp.id, active: !cp.active })}
                    >
                      {cp.active ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 px-1">Recent scans</h3>
          {logs.isLoading ? (
            [1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)
          ) : (logs.data ?? []).length === 0 ? (
            <Card><CardContent className="p-4 text-sm text-slate-500">No scans yet.</CardContent></Card>
          ) : (
            (logs.data ?? []).slice(0, 15).map((log) => (
              <Card key={log.id}>
                <CardContent className="p-3 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-emerald-600 flex-none" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{log.checkpoint.name}</p>
                    <p className="text-xs text-slate-500">{log.guard.full_name} · {formatDateTime(log.scanned_at)}</p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New patrol checkpoint</DialogTitle>
          </DialogHeader>
          <form className="space-y-3" onSubmit={form.handleSubmit((d) => createMut.mutate(d))}>
            <div>
              <Label htmlFor="cp-name">Name</Label>
              <Input id="cp-name" placeholder="Main Gate" {...form.register('name')} />
              {form.formState.errors.name ? <p className="text-xs text-red-600 mt-1">{form.formState.errors.name.message}</p> : null}
            </div>
            <div>
              <Label htmlFor="cp-loc">Location label</Label>
              <Input id="cp-loc" placeholder="Society front entrance" {...form.register('location_label')} />
            </div>
            <div>
              <Label htmlFor="cp-pos">Position in route</Label>
              <Input id="cp-pos" type="number" min={0} {...form.register('position_index')} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMut.isPending}>Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR reveal dialog */}
      <Dialog open={!!qrFor} onOpenChange={(v) => !v && setQrFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" /> Printable QR · {qrFor?.name}
            </DialogTitle>
          </DialogHeader>
          {qrFor ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Print this token onto a label and stick it at the checkpoint. Generate the QR image with any standard library.
              </p>
              <pre className="text-xs bg-slate-100 p-3 rounded border border-slate-200 overflow-x-auto whitespace-pre-wrap break-all">
                {qrFor.token}
              </pre>
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (qrFor) {
                      navigator.clipboard?.writeText(qrFor.token);
                      toast.success('Copied');
                    }
                  }}
                >
                  Copy token
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
